import fb from '../../firebase'
import Vue from 'vue'
import { isEqual, reduce, uniq } from 'lodash'
import Axios from 'axios'
import {
  AssetsState,
  FirestoreDocSnapper,
  FirestoreQuerySnapper,
  FirestoreSnapperType,
  RootState,
} from '../types'
import store, { moduleActionContext, storehelper } from '..'
import {
  Asset,
  AssetFolderQuery,
  AssetGroup,
  AssetGroupErrors,
  AssetGroupType,
  AssetSummary,
  AssetType,
  InstagramAsset,
  SocialMediaAssetTypes,
  SocialMediaTypes,
  TweetAsset,
  TweetPollAsset,
  VideoAsset
} from '@/types/assets'
import { firestore } from 'firebase/app'
import InstagramManager from '@/classes/InstagramManager'
import { ActionContext } from 'vuex'
import QuerySnapshot = firebase.firestore.QuerySnapshot
import QueryDocumentSnapshot = firebase.firestore.QueryDocumentSnapshot
import { Layout } from '@/types/layouts'
import DocumentSnapshot = firebase.firestore.DocumentSnapshot
import { CollectionNames, KeyDict } from '@/types'
import { lazyUpdate } from '../storehelper'

type AssetsContext = ActionContext<AssetsState, RootState>

const MAX_ASSETS_IN_FOLDER = 60
const UPDATE_TIME_QUEUE = 500
const MAX_CACHED_OPEN_FOLDERS = 8

interface AssetQueueItem {
  action: 'add' | 'remove',
  data: Asset,
  assetGroupId: string
}
let assetQueue: AssetQueueItem[] = []

const updateSingleAsset = (item: AssetQueueItem) => {
  const existingAsset = store.state.assets.assetGroupAssets[item.assetGroupId][item.data.id]
  if (existingAsset === undefined && item.action === 'add') {
    store.commit.assets.addAssetToGroup({ asset: item.data, assetGroupId: item.assetGroupId })
  }
  if (existingAsset !== undefined && item.action === 'add' && !isEqual(existingAsset, item.data)) {
    store.commit.assets.updateAssetInGroup({ asset: item.data, assetGroupId: item.assetGroupId })
  }
  if (existingAsset !== undefined && item.action === 'remove') {
    store.commit.assets.removeAssetInGroup({ assetId: item.data.id, assetGroupId: item.assetGroupId })
  }
}

let stopAssetQueue = false

const checkAssetQueue = () => {
  if (stopAssetQueue) return
  if(assetQueue.length > 0) {
    const item = assetQueue[0]
    updateSingleAsset(item)
    assetQueue.shift()
  }
  setTimeout(() => {
    checkAssetQueue()
  }, UPDATE_TIME_QUEUE)
}

checkAssetQueue()
const stopCheckAssetQueue = () => {
  stopAssetQueue = true
}

const makeAssetGroupQuery = (parents: string[], teamId: string): firestore.Query<firestore.DocumentData> => {
  if (parents.length > 10) {
    throw new Error(`makeQueryAssetGroup, parents should be smaller then 10 ids ${parents.length}`)
  }
  return fb.db.collection('asset-groups')
           .where('team_id', '==', teamId)
           .where('parent_id', 'in', parents)
           .where('deleted', '==', false)
}

const assetsModule = {
  namespaced: true,
  state: {
    initializing: false,
    allAssets: {},
    sceneAssets: {},
    assetGroupList: {},
    assetGroupAssets: {},
    subFolders: {},
    folderList: {},
    selectedFolder: '',
    folderAssetsLoading: true,
    openFolders: [AssetGroupType.RootFolder]
  } as AssetsState,
  getters: {
    activeFolder: (state: AssetsState): AssetGroup | undefined => {
      const folderId = state.selectedFolder
      if (folderId === null) return undefined
      if (folderId === AssetGroupType.LiveStream) {
        return {
          id: folderId,
          name: AssetGroupType.LiveStream
        }
      }
      return state.folderList[folderId]
    },
    initializing: (state: AssetsState): boolean => {
      return state.initializing
    }
  },
  actions: {
    subscribeAssetFolderAssets (context: AssetsContext, query: AssetFolderQuery) {
      const { state, commit, dispatch } = assetsModuleActionContext(context)
      const assetGroup = state.folderList[query.assetGroupId]
      if (!assetGroup) {
        // => don't subscribe the root folder
        return
      }
      if (assetGroup.type === AssetGroupType.LiveStream) {
        throw Error(`Cannot subscribe to the livestream assetgroup`)
      }
      const prefix = `assetgroup_${query.assetGroupId}_${query.teamId}_`
      const runningkeys = storehelper.runningKeys(prefix)

      const key = `${prefix}${query.minStars}`
      for (const rkey of runningkeys) {
        if (rkey !== key) {
          const stars = parseInt(rkey.replace(prefix, ''))
          const newQuery: AssetFolderQuery = {
            ...query,
            minStars: stars
          }
          dispatch.unsubscribeAssetFolderAssets(newQuery)
        }
      }
      let teamId = store.state.user.teamId
      if (store.state.user.profile && store.state.user.profile.role === 'superuser') {
        teamId = store.state.user.profile.currentTeam || teamId
      }

      // store.commit.assets.assetsLoading(true)
      const snapper: FirestoreQuerySnapper = {
        query: fb.db.collection(CollectionNames.ASSETS)
                .where('team_id', '==', teamId)
                .where('asset_group', '==', query.assetGroupId)
                .where('stars', '>=', query.minStars)
                .where('deleted', '==', false)
                .orderBy('stars', 'desc')
                .orderBy('timestamp', 'desc')
                .limit(MAX_ASSETS_IN_FOLDER),
        mutation: commit.updateAssetgroupQuery,
        type: FirestoreSnapperType.Query
      }
      storehelper.subscribe(key, snapper)
    },
    unsubscribeAssetFolderAssets (_context: AssetsContext, query: AssetFolderQuery) {
      const prefix = `assetgroup_${query.assetGroupId}_${query.teamId}_`
      const key = `${prefix}${query.minStars}`
      store.commit.assets.cleanUpAssetGroup(query.assetGroupId)
      storehelper.unsubscribe(key, () => { store.commit.assets.cleanUpAssetGroup(query.assetGroupId) })
    },
    subscribeAssetFolderSubfolders (context: AssetsContext, query: AssetFolderQuery) {
      const { state, commit, dispatch } = assetsModuleActionContext(context)
      const assetGroup = state.folderList[query.assetGroupId]
      const isRootLevel = !assetGroup
      const prefix = `assetgroup_${query.assetGroupId}_${query.teamId}_`
      const subfolderKey = `${prefix}subfolders`
      const runningkeys = storehelper.runningKeys(prefix)
      for (const rkey of runningkeys) {
        if (rkey === subfolderKey) {
          dispatch.unsubscribeAssetFolderSubfolders(query)
        }
      }
      const folderId = (isRootLevel) ? '' : query.assetGroupId
      const subfolderSnapper: FirestoreQuerySnapper = {
        query: fb.db.collection(CollectionNames.ASSET_GROUPS)
          .where('team_id', '==', query.teamId)
          .where('parent_id', '==', folderId)
          // .where('type', '==', AssetGroupType.Folder)
          .where('deleted', '==', false)
          .limit(MAX_ASSETS_IN_FOLDER),
        mutation: commit.setSubFolders,
        type: FirestoreSnapperType.Query
      }
      storehelper.subscribe(subfolderKey, subfolderSnapper)
    },
    unsubscribeAssetFolderSubfolders (_context: AssetsContext, query: AssetFolderQuery ) {
      const prefix = `assetgroup_${query.assetGroupId}_${query.teamId}_`
      const subfolderKey = `${prefix}subfolders`
      store.commit.assets.cleanUpSubFolders(query.assetGroupId)
      storehelper.unsubscribe(subfolderKey, () => { store.commit.assets.cleanUpSubFolders(query.assetGroupId) })
    },
    unsubscribeTeamAssetGroup (_context, teamId: string) {
      if (teamId === '') return
      const key = `teamassetgroups_${teamId}`
      storehelper.unsubscribe(key)
    },
    async subscribeTeamAssetGroup (context: AssetsContext, teamId: string) {
      if (teamId === '') return
      const { state } = assetsModuleActionContext(context)
      const key = `teamassetgroups_${teamId}`
      const snapper: FirestoreQuerySnapper = {
        query: makeAssetGroupQuery(state.openFolders, teamId),
        mutation: store.commit.assets.updateTeamAssetGroups,
        type: FirestoreSnapperType.Query
      }
      await storehelper.subscribe(key, snapper)
    },
    clearTeamAssetGroups (_context) {
      store.commit.assets.clearTeamAssetGroups()
    },
    addFolderToSubscribedTeamAssetGroups (context: AssetsContext, folderId: string) {
      const { state, commit } = assetsModuleActionContext(context)
      commit.addOpenFolder(folderId)
      const teamId = store.state.team.activeTeam
      const key = `teamassetgroups_${teamId}`
      const query = makeAssetGroupQuery(state.openFolders, teamId)
      if (storehelper.exists(key)) storehelper.updateQuery(key, query)
    },
    async subscribeAssetGroups (context: AssetsContext, assetGroups: KeyDict<boolean>) {
      const { commit } = assetsModuleActionContext(context)
      for (const prop in assetGroups) {
        const snapper: FirestoreDocSnapper = {
          doc: fb.db.collection(CollectionNames.ASSET_GROUPS).doc(prop),
          mutation: commit.updateAssetGroup,
          type: FirestoreSnapperType.Document
        }
        storehelper.subscribe(`assetgroups_${prop}`, snapper)
      }
    },
    async unsubscribeAssetGroups (_context: AssetsState, assetGroups: KeyDict<boolean>) {
      for (const prop in assetGroups) {
        storehelper.unsubscribe(`assetgroups_${prop}`)
      }
    },
    addSceneAssetToLayout (_context, asset: Asset) {
      store.commit.assets.addSceneAssetToLayout(asset)
    },
    updateLayoutAssets (_context, layout: Layout) {
      const contents = layout.contents || {}
      const requiredAssetIds = reduce(contents, (result: string[], content, _key) => {
        result.push(content.id)
        return result
      }, [])

      for (const id of requiredAssetIds) {
        fb.db.collection(CollectionNames.ASSETS).doc(id)
          .get()
          .then((doc: DocumentSnapshot) => {
            store.commit.assets.updateSceneAsset(doc)
          })
          .catch(error => {
            console.error('Error getting assets: ', error)
          })
      }
    },

    // Check for unsubscribes
    updateSceneAssets (_context) {
      const currentCast = store.state.events.currentCast
      let requiredAssetIds: string[] = []
      const prefix = 'asset_'
      if (currentCast.scenes) {
        Object.values(currentCast.scenes).forEach(scene => {
          // add scene content
          if (scene.contents !== undefined) {
            Object.values(scene.contents).forEach(content => {
              if (![AssetType.Playlist, AssetType.Broadcast, AssetType.Caster].includes(content.type)) {
                if (content.id) {
                  requiredAssetIds.push(content.id)
                }
              }
            })

            // Make sure assets are available in preview
            // NOTE: we load all assets in preview to have them available ASAP
            // NOTE2: apparently this updateSceneAssets methods is called multiple times during a cast
            if (scene.id) {
              const previewScene = currentCast.preview_scenes[scene.id]
              if (previewScene && previewScene.scene_layout && previewScene.scene_layout.contents) {
                const ids: string[] = Object.keys(previewScene.scene_layout.contents)
                requiredAssetIds = uniq(requiredAssetIds.concat(ids))
              }
            }
          }
        })
      }
      for (const id of requiredAssetIds) {
        const subid = `${prefix}${id}`
        if (!storehelper.exists(subid)) {
          const snapper: FirestoreDocSnapper = {
            doc: fb.db.collection(CollectionNames.ASSETS).doc(id),
            mutation: store.commit.assets.updateSceneAsset,
            type: FirestoreSnapperType.Document
          }
          // If the document is gone, don't
          storehelper.subscribe(subid, snapper, async error => {
            const asset = await fb.db.collection(CollectionNames.ASSETS).doc(id).get()
            if (asset.exists) {
              console.error(`Subscribing to asset ${id}, it exists, but errors `, error)
            }
          })
        }
      }
      const runningKeys = storehelper.runningKeys(prefix)
      for (const id of runningKeys) {
        if (!requiredAssetIds.includes(id.substr(prefix.length))) {
          const cleanup = () => {
            store.commit.assets.removeFromSceneAsset(id.substr(prefix.length))
          }
          storehelper.unsubscribe(id, cleanup)
        }
      }
    },
    async updateInstagramWithUsername (_context, assetId: string) {
      let asset = store.state.assets.sceneAssets[assetId]
      if (!asset) {
        for (const assetgroup of Object.values(store.state.assets.assetGroupAssets)) {
          if (assetgroup[assetId]) {
            asset = assetgroup[assetId]
          }
        }
      }
      const userdata = asset.data
      if (asset.type === AssetType.Instagram && userdata && !userdata.data) {
        const user = await InstagramManager.getUser(asset.data.url)
        const igasset = JSON.parse(JSON.stringify(asset)) as InstagramAsset
        if (typeof igasset.data !== 'string') {
          if (igasset.data !== null && user !== null) {
            igasset.data.user = user
          }
        }
        igasset.id = assetId
        await store.dispatch.assets.updateAsset(igasset)
      }
    },
    async updateAssetStars (_context: AssetsContext, asset: { id: string, stars: number }) {
      await fb.db.collection(CollectionNames.ASSETS).doc(asset.id).set({ stars: asset.stars }, { merge: true })
    },
    async updateAssetTransitionDelay (_context: AssetsContext, asset: { id: string, delay: number }) {
      await fb.db.collection(CollectionNames.ASSETS).doc(asset.id).set({ transitionDelay: asset.delay }, { merge: true })
    },
    async updateAssetVolume (_context: AssetsContext, asset: { id: string, volume: number }) {
      await fb.db.collection(CollectionNames.ASSETS).doc(asset.id).set({ volume: asset.volume }, { merge: true })
    },
    async updateAsset (_context, asset: Partial<Asset>) {
      const saveAsset = { ...asset }
      if (saveAsset.type === AssetType.Instagram || saveAsset.type === AssetType.Tweet) {
        saveAsset.data = JSON.stringify(saveAsset.data)
      }
      await fb.db.collection(CollectionNames.ASSETS).doc(saveAsset.id).set(saveAsset, { merge: true })
    },
    async updateAssetGroup (_context, folder: Partial<AssetGroup>) {
      const saveFolder = { ...folder }
      await fb.db.collection(CollectionNames.ASSET_GROUPS).doc(saveFolder.id).set(saveFolder, { merge: true })
    },
    cleanupSceneAssets (_context) {
      const prefix = 'asset_'
      const runningKeys = storehelper.runningKeys(prefix)
      for (const id of runningKeys) {
        const cleanup = () => {
          store.commit.assets.removeFromSceneAsset(id.substr(prefix.length))
        }
        storehelper.unsubscribe(id, cleanup)
      }
    },
    async createAssetFolder (_context, folder: AssetGroup) {
      const group = {
        timestamp: Date.now(),
        deleted: false,
        assets: {},
        ...folder
      }
      try {
        const docRef = await fb.db.collection(CollectionNames.ASSET_GROUPS).add(group)
        if (folder.hasOwnProperty('cast_id')) {
          await fb.db.collection(CollectionNames.CASTS).doc(folder.cast_id).set({
              asset_groups: {
                [docRef.id]: true
              }
            }, {
              merge: true
            })
          }
      } catch (error) {
        console.error('Error adding group: ', error)
      }
    },
    async createTwitterPoll (_context, poll: TweetPollAsset) {
      const asset = {
        ...poll,
        deleted: false,
        stars: 0,
        question: 'Loading...',
        timestamp: (new Date()).getTime(),
      }

      try {
        const doc = await fb.db.collection(CollectionNames.ASSETS).add(asset)
        await fb.db.collection(CollectionNames.ASSETS).doc(doc.id).set({ id: doc.id }, { merge: true })
      } catch(error) {
        console.error('Error createTwitterPoll ', error)
      }

    },
    async createTweetAsset (_context, tweet: TweetAsset) {
      const asset = {
        ...tweet,
        deleted: false,
        stars: 5,
        timestamp: (new Date()).getTime(),
      }
      try {
        const doc = await fb.db.collection(CollectionNames.ASSETS).add(asset)
        await fb.db.collection(CollectionNames.ASSETS).doc(doc.id).set({ id: doc.id }, { merge: true })
      } catch(error) {
        console.error('Error createTweetAsset ', error)
      }
    },
    async reloadWidget (_context: AssetsContext, widgetId: string) {
      await fb.db.collection(CollectionNames.ASSETS).doc(widgetId).set({
        reloadKey: (new Date()).getTime()
      }, { merge: true })
    },
    async createInstagramAsset (context: AssetsContext, post: InstagramAsset) {
      const { dispatch } = assetsModuleActionContext(context)
      const asset: InstagramAsset = {
        ...post,
        deleted: false,
        stars: 5,
        timestamp: (new Date()).getTime()
      }
      if (asset.url) {
        try {
          const response = await Axios.get(asset.url)
          const postData = JSON.parse(response.data.split('window._sharedData = ')[1].split(';</script>')[0])
          const igPost = postData.entry_data.PostPage[0].graphql.shortcode_media
          asset.thumbnail = igPost.display_url
          asset.data = JSON.stringify({
            id: igPost.id,
            text: igPost.edge_media_to_caption.edges[0].node.text,
            owner: igPost.owner.id,
            media_url: igPost.display_url,
            url: asset.url,
            media_type: 'image',
            permalink: asset.url
          })
          const asset_doc = await fb.db.collection(CollectionNames.ASSETS).add(asset)
          await fb.db.collection(CollectionNames.ASSETS).doc(asset_doc.id).set({ id: asset_doc.id }, { merge: true })
          await dispatch.updateInstagramWithUsername(asset_doc.id)
        } catch (error) {
          console.error('Trying to parse instagram, but failed ', error)
        }
      }
    },
    async createAsset (_context, asset: Partial<Asset>) {
      const newAsset = {
        timestamp: Date.now(),
        stars: 0,
        deleted: false,
        ...asset
      }
      try {
        const docref = fb.db.collection(CollectionNames.ASSETS).doc()
        newAsset.id = docref.id
        const docRef = await docref.set(newAsset)
        return docRef
      } catch(error) {
        console.error('Error creating asset: ', error)
      }
    },
    async updateFolderName (_context, folder: AssetGroup) {
      try {
        await fb.db.collection(CollectionNames.ASSET_GROUPS).doc(folder.id).set(folder, { merge: true })
      } catch(error) {
        console.log('Error updating folder name: ', error)
      }
    },
    async deleteAssetFolder (context: AssetsContext, { folder, deepDelete = true }: { folder: AssetGroup, deepDelete: boolean }) {
      const { commit, getters, dispatch } = assetsModuleActionContext(context)
      console.log('XXX deleteAssetFolder ', folder.id)

      // we need team id to be able to enforce CloudCast Firestore rules
      // (= user is part of a specific team)
      let teamId = store.state.user.teamId
      if (store.state.user.profile && store.state.user.profile.role === 'superuser') {
        teamId = store.state.user.profile.currentTeam || teamId
      }
      const isSocialMediaFolder = !!folder && SocialMediaTypes.some(type => folder.type === type)
      const checkFolderAssets = isSocialMediaFolder === false
      commit.deleteFolder(folder.id)

      if (!deepDelete) {
        // folder should not have any nested folders or assets
        // allowed to be ANY assetGroup type (folder, handle, etc...)
        const folderRef: QuerySnapshot = await fb.db.collection('asset-groups')
          .where('team_id', '==', teamId)
          .where('parent_id', '==', folder.id)
          .where('deleted', '==', false)
          .limit(1)
          .get()

        if (!folderRef.empty) {
          throw new Error(AssetGroupErrors.FolderNotEmpty)
        }

        if (checkFolderAssets) {
          const assetRef: QuerySnapshot = await fb.db.collection(CollectionNames.ASSETS)
            .where('team_id', '==', teamId)
            .where('asset_group', '==', folder.id)
            .where('deleted', '==', false)
            .limit(1)
            .get()

          if (!assetRef.empty) {
            throw new Error(AssetGroupErrors.FolderNotEmpty)
          }
        }
      }

      try {
        if (deepDelete && checkFolderAssets) { //Delete assets & subfolders first
          const folderRef: QuerySnapshot = await fb.db.collection('asset-groups')
                                                   .where('team_id', '==', teamId)
                                                   .where('parent_id', '==', folder.id)
                                                   .where('deleted', '==', false)
                                                   .get()

          if (!folderRef.empty) {
            folderRef.docs.forEach(subfolder => {
              dispatch.deleteAssetFolder({
                folder: { id: subfolder.id, ...subfolder.data() } as AssetGroup,
                deepDelete: true
              }); //Recursively delete subfolder & its contents
            });
          }
          const assetRef: QuerySnapshot = await fb.db.collection('assets')
                                                     .where('team_id', '==', teamId)
                                                     .where('asset_group', '==', folder.id)
                                                     .where('deleted', '==', false)
                                                     .get()
          if (!assetRef.empty) {
            assetRef.docs.forEach(subAsset => { //Delete all assets in folder
              fb.db.collection('assets').doc(subAsset.id).set({ deleted: true }, { merge: true })
            });
          }
        }

        // Delete folder
        const activeFolder = getters.activeFolder
        await fb.db.collection('asset-groups').doc(folder.id).set({ deleted: true }, { merge: true })
        store.dispatch.castTemplates.cleanupTeamTemplates({ id: folder.id, itemType: 'asset_groups' })
        console.log('Deleted folder activeFolder?', activeFolder)
        if (activeFolder && activeFolder.id === folder.id) {
          console.log('Deleted folder - select new folder')
          commit.selectFolder(activeFolder.parent_id || '')
        }
      } catch(error) {
        console.log('Error deleting folder: ', error)
      }
    },
    async deleteAsset (_context: AssetsContext, assetId: string) {
      await fb.db.collection(CollectionNames.ASSETS).doc(assetId).set({ deleted: true }, { merge: true })
    },
    async selectFolder (context: AssetsContext, id: string) {
      const assetgroups = store.state.assets.folderList
      const { commit } = assetsModuleActionContext(context)
      let selectedFolder = ''
      if (id !== '' && assetgroups[id] !== undefined) {
        selectedFolder = id
      }
      commit.selectFolder(selectedFolder)
    },
    async retranscodeClip (_context: AssetsContext, assetId: string) {
      await fb.db.collection(CollectionNames.ASSETS).doc(assetId).set({
        progress: 0,
        state: 'transcoding'
      }, { merge: true })
    }
  },
  mutations: {
    updateAssetGroup (state: AssetsState, doc: firestore.DocumentSnapshot) {
      state.folderList[doc.id] = doc.data() as AssetGroup // is this still relevant?
    },
    updateSceneAsset (state: AssetsState, snap: firestore.DocumentSnapshot) {
      const asset = snap.data()
      if (asset) {
        if (asset.data && typeof asset.data === 'string') {
          asset.data = JSON.parse(asset.data)
        }
        Vue.set(state.sceneAssets, snap.id, asset)
      }
    },
    addSceneAssetToLayout (state: AssetsState, asset: Asset) {
      Vue.set(state.sceneAssets, asset.id, asset)
    },
    assetsLoading (state: AssetsState, isLoading: boolean) {
      state.folderAssetsLoading = isLoading
    },
    removeFromSceneAsset (state: AssetsState, assetId: string) {
      Vue.delete(state.sceneAssets, assetId)
    },
    clearTeamAssetGroups (state: AssetsState) {
      state.initializing = true
      state.selectedFolder = AssetGroupType.RootFolder
      Vue.set(state, 'assetGroupAssets', {})
      Vue.set(state, 'assetGroupList', {})
      Vue.set(state, 'folderList', {})
    },
    updateTeamAssetGroups (state: AssetsState, snap: firestore.QuerySnapshot) {
      state.initializing = false
      if (snap) {
        snap.docs.forEach(assetgroup => {
          const assetgroupdata = assetgroup.data() as AssetGroup
          lazyUpdate(state.folderList, assetgroup.id, { ...assetgroupdata, id: assetgroup.id })
          if (state.folderList[assetgroup.id].deleted !== false) {
            Vue.delete(state.folderList, assetgroup.id)
          }
        })
      } else {
        state.folderList = {}
      }
    },
    deleteFolder (state: AssetsState, assetGroupId: string) {
      if (state.folderList[assetGroupId] !== undefined) {
        Vue.delete(state.folderList, assetGroupId)
      }
    },
    updateAssetgroupQuery (state: AssetsState, snap: firestore.QuerySnapshot) {
      assetQueue = []
      let assetGroupId = state.selectedFolder
      if (snap) {
        const keys = {}
        if (snap.docs.length) {
          snap.docs.forEach((doc: QueryDocumentSnapshot, idx: number) => {
            const asset = doc.data() as Asset
            if (typeof asset.data === 'string') {
              asset.data = JSON.parse(asset.data)
            }
            asset.id = doc.id
            assetGroupId = asset.asset_group
            keys[asset.id] = true
            if (!!asset.asset_group && state.assetGroupAssets[asset.asset_group] === undefined) {
              Vue.set(state.assetGroupAssets, asset.asset_group, {})
              Vue.set(state.assetGroupList, asset.asset_group, {})
            }
            const existingAsset = state.assetGroupAssets[asset.asset_group][asset.id]
            if (existingAsset === undefined || !isEqual(existingAsset, asset)) {
              assetQueue.push({ data: asset, assetGroupId: assetGroupId, action: 'add' })

              // do at least one asset check to immediately signal some assets have been found
              // eg Filespace would otherwise show the "No assets..." text for a fraction of a second
              // skip this for social media assets (big updates freeze the UI)
              const firstMatch = idx === 0
              const isSocialMedia = (SocialMediaAssetTypes.some(type => asset.type === type))
              if (firstMatch && !isSocialMedia) {
                checkAssetQueue()
              }
            }
          })

        } else {
          // empty query - no more assets
          // state.assetGroupAssets[assetGroupId] = {}
          Vue.set(state.assetGroupAssets, assetGroupId, {})
          Vue.set(state.assetGroupList, assetGroupId, {})
        }

        if (assetGroupId) {
          for (const existingKey of Object.keys(state.assetGroupAssets[assetGroupId])) {
            if (!keys[existingKey]) {
              assetQueue.push({ data: state.assetGroupAssets[assetGroupId][existingKey], assetGroupId, action: 'remove' })
            }
          }
        }

        store.commit.assets.assetsLoading(false)
      }
    },
    cleanUpAssetGroup (state: AssetsState, assetGroupId: string ) {
      Vue.set(state.assetGroupAssets, assetGroupId, {})
      Vue.set(state.assetGroupList, assetGroupId, {})
    },
    addAssetToGroup (state: AssetsState, item: { asset: Asset, assetGroupId: string }) {
      Vue.set(state.assetGroupAssets[item.assetGroupId], item.asset.id, { ...item.asset })
      const assetSummary: AssetSummary = {
        id: item.asset.id,
        url: item.asset.url,
        name: item.asset.name,
        type: item.asset.type,
        timestamp: item.asset.timestamp,
        stars: item.asset.stars
      }
      if (item.asset.type === AssetType.Video) {
        const videoAsset = item.asset as VideoAsset
        assetSummary.state = videoAsset.state
      }
      Vue.set(state.assetGroupList[item.assetGroupId], assetSummary.id, assetSummary)
    },
    updateAssetInGroup (state: AssetsState, item: { asset: Asset, assetGroupId: string }) {
      const orig_keys = Object.keys(state.assetGroupAssets[item.assetGroupId][item.asset.id])
      const new_keys = Object.keys(item.asset)
      for (const key of orig_keys) {
        if (!new_keys.includes(key)) {
          Vue.delete(state.assetGroupAssets[item.assetGroupId][item.asset.id], key)
        }
      }
      for (const key of new_keys) {
        Vue.set(state.assetGroupAssets[item.assetGroupId][item.asset.id], key, item.asset[key])
      }
      const assetSummary: AssetSummary = {
        id: item.asset.id,
        url: item.asset.url,
        name: item.asset.name,
        type: item.asset.type,
        timestamp: item.asset.timestamp,
        stars: item.asset.stars
      }
      if (item.asset.type === AssetType.Video) {
        const videoAsset = item.asset as VideoAsset
        assetSummary.state = videoAsset.state
      }
      const existingSummary = state.assetGroupList[item.assetGroupId][item.asset.id]
      if (!isEqual(assetSummary, existingSummary)) {
        Vue.set(state.assetGroupList[item.assetGroupId], item.asset.id, assetSummary)
      }
    },
    removeAssetInGroup (state: AssetsState, item: { assetId: string, assetGroupId: string }) {
      Vue.delete(state.assetGroupAssets[item.assetGroupId], item.assetId)
      Vue.delete(state.assetGroupList[item.assetGroupId], item.assetId)
    },
    selectFolder (state: AssetsState, id: string) {
      state.selectedFolder = id
      state.subFolders = {}
    },
    setSubFolders (state: AssetsState, snap: firestore.QuerySnapshot) {
      const nestedGroups: KeyDict<AssetGroup> = {}
      snap.docs.forEach(assetgroup => {
        const assetgroupdata = assetgroup.data()
        nestedGroups[assetgroup.id] = ({ id: assetgroup.id, ...assetgroupdata } as AssetGroup)
      })
      Vue.set(state, 'subFolders', nestedGroups)
    },
    cleanUpSubFolders (state: AssetsState, _assetGroupId: string ) {
      Vue.set(state, 'subFolders', {})
    },
    clearOpenFolders (state: AssetsState) {
      state.openFolders = [AssetGroupType.RootFolder]
    },
    addOpenFolder (state: AssetsState, folder: string) {
      if (state.openFolders.length > 0 && state.openFolders[state.openFolders.length - 1] === folder) {
        // everythin is already optimal, dont need to add this
        return
      }
      state.openFolders.push(folder)
      // dont remove the first element as this is the rootfolder
      if (state.openFolders.length > MAX_CACHED_OPEN_FOLDERS) {
        state.openFolders.splice(1, 1)
      }
    }
  }
} as const

export default assetsModule
export const assetsModuleActionContext = context => moduleActionContext(context, assetsModule)
export { stopCheckAssetQueue }
